import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { errorAlertMessage } from '../../common/views/ErrorAlert/errorAlertMessage';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useSmartLinkPageFacts } from '../pageActions/useSmartLinkPageFacts';
import { deleteLossRows, isConfirmedName } from './deleteLossRows';
import { SMLDeleteLossList } from './SMLDeleteLossList';
import { SMLDeleteConfirmField } from './SMLDeleteConfirmField';
import { useExportPresaves } from '../presave/useExportPresaves';
import { LockedControl } from '../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import styles from './SMLDeletePageSheet.module.scss';

interface SMLDeletePageSheetProps {
  accountId: number;
  smartLink: SerializedSmartLink;
  closeModal: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
  error?: ErrorAPIResult['error'];
}

/**
 * The confirmation, told the truth (design dialogs a10).
 *
 * "Are you sure?" named nothing, listed nothing and painted the destructive
 * button in the same violet as every safe one. This dialog names the page,
 * counts what dies with it from the numbers the screen already holds, and asks
 * the artist to type the name — but only when something is at stake. A draft
 * that was never published and collected nothing gets a plain question, since
 * ceremony over nothing teaches people to click through ceremony.
 *
 * The parent mounts it only while the dialog is open, so the dialog itself is always open.
 */
const SMLDeletePageSheetComponent: React.FC<SMLDeletePageSheetProps> = ({
  accountId,
  smartLink,
  closeModal,
  onDelete,
  isDeleting = false,
  error,
}) => {
  const intl = useIntl();
  const [typed, setTyped] = useState('');
  const facts = useSmartLinkPageFacts(accountId, smartLink);
  const { name } = smartLink.recentSmartLinkVersion;
  // Deleting the page deletes the list with it, so the way out is offered here
  // rather than only in the menu the artist has already left.
  const {
    exportPresaves,
    canExport,
    isLocked: isExportLocked,
  } = useExportPresaves({ accountId, campaignUuid: facts.campaignUuid });

  const rows = useMemo(
    () =>
      deleteLossRows({
        isPublished: facts.isPublished,
        shareUrl: smartLink.shareUrl,
        pendingPresaveCount: facts.pendingPresaveCount,
        viewCount: facts.viewCount,
        emailCount: facts.emailCount,
      }),
    [facts.isPublished, facts.pendingPresaveCount, facts.viewCount, facts.emailCount, smartLink.shareUrl]
  );

  const isMatch = isConfirmedName(typed, name);
  const onTyped = useCallback((value: string) => setTyped(value), []);

  const hasStakes = rows.length > 0;
  const title = intl.formatMessage({ id: 'app.sml.delete.title', defaultMessage: 'Delete “{name}”?' }, { name });
  const exportLabel = intl.formatMessage({ id: 'app.sml.delete.export_link', defaultMessage: 'Download CSV' });

  const footerNote =
    !facts.isLoading && canExport ? (
      <p className={styles.footerNote}>
        {intl.formatMessage({
          id: 'app.sml.delete.export_question',
          defaultMessage: 'Exporting the pre-save list first?',
        })}{' '}
        {isExportLocked ? (
          <LockedControl
            variant="link"
            feature={SMLUpgradeFeature.fanExport}
            onClick={exportPresaves}
            label={exportLabel}
          />
        ) : (
          <button type="button" className={styles.footerLink} onClick={exportPresaves}>
            {exportLabel}
          </button>
        )}
      </p>
    ) : undefined;

  const stakesDescription = intl.formatMessage({
    id: 'app.sml.delete.description',
    defaultMessage: "This can't be undone. The page, its address and everything it has collected are removed.",
  });
  const draftDescription = intl.formatMessage({
    id: 'app.sml.delete.description_draft',
    defaultMessage: "It's still a draft — never published, no views, nothing collected. Nothing else is affected.",
  });
  const description = hasStakes ? stakesDescription : draftDescription;

  return (
    <ConfirmDialog
      isOpen
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={hasStakes ? faTriangleExclamation : faTrash} />}
      title={title}
      description={facts.isLoading ? undefined : description}
      cancelLabel={intl.formatMessage({ id: 'app.sml.delete.keep', defaultMessage: 'Keep page' })}
      confirmLabel={
        hasStakes
          ? intl.formatMessage({ id: 'app.sml.delete.confirm', defaultMessage: 'Delete permanently' })
          : intl.formatMessage({ id: 'app.sml.delete.confirm_draft', defaultMessage: 'Delete' })
      }
      isBusy={isDeleting}
      isConfirmDisabled={facts.isLoading || (hasStakes && !isMatch)}
      error={error === undefined ? undefined : errorAlertMessage(intl, error)}
      footerNote={footerNote}
      onConfirm={onDelete}
      onCancel={closeModal}
      testId="smartlink-delete-dialog"
    >
      {facts.isLoading && <LoadingView />}
      {!facts.isLoading && hasStakes && <SMLDeleteLossList rows={rows} shareUrl={smartLink.shareUrl} />}
      {!facts.isLoading && hasStakes && (
        <SMLDeleteConfirmField name={name} value={typed} isMatch={isMatch} onChange={onTyped} />
      )}
      {!facts.isLoading && !hasStakes && (
        <p className={styles.fineprint}>
          {intl.formatMessage({ id: 'app.sml.delete.fineprint', defaultMessage: "This can't be undone." })}
        </p>
      )}
    </ConfirmDialog>
  );
};

export const SMLDeletePageSheet = TypedMemo(SMLDeletePageSheetComponent);
